/* eslint-disable jsx-a11y/media-has-caption */
import { Column } from 'primereact/column';
import { DataTable } from 'primereact/datatable';
import { Button } from 'primereact/button';
import { Divider } from 'primereact/divider';
import { Dropdown } from 'primereact/dropdown';
import React, { useEffect, useRef, useState } from 'react';
import { useSelector } from 'react-redux';
import { useGetRecordingHistoryQuery, useGetRecordingStreamQuery } from './recordingHistorySlice';
import { selectCurrentEmpid, selectCurrentUser, selectIsAllowCoachingActionModel } from '../../../pages/login/authSlice';
import ReadConversationLog from '../conversationLogs/readConversationLog/ReadConversationLog';
import NewConversationLog from '../conversationLogs/newConversationLog/NewConversationLog';
import { CoachingEntityTypes, CoachingTabs } from '../../../utils/typeConstants';
import UpdateStrength from '../strengths/updateStrength/UpdateStrength';
import NewStrength from '../strengths/newStrength/NewStrength';
import ReadStrengths from '../strengths/readStrengths/ReadStrengths';
import { StrengthList } from '../strengths/types';
import { datetimeToDate, formatDate3 } from '../../../utils/dateFormat';
import UserConsentDialog from './UserConsentDialog';
import { useGetAIExtractRecordingInsightsMutation } from '../../../components/vertexAI/vertexAISlice';
import { AiMode } from '../../../components/vertexAI/vertexAIType';
import { LoadingSpinner } from '../../../components/loadingSpinner/loadingspinner';
import RecordingDialog from './RecordingDialog';
import CAPCreationDialog from './CAPCreationDialog';

export const RecordingHistory = () => {
    const [AIExtractRecordingInsights] = useGetAIExtractRecordingInsightsMutation();
    const loggedEmpNum = useSelector(selectCurrentEmpid);
    const loggedEmpUserName = useSelector(selectCurrentUser);
    const isAllowCoachingActionModel = useSelector(selectIsAllowCoachingActionModel);

    const [currentRowData, setCurrentRowData] = useState<any>();
    const [viewState, setViewState] = useState<string>('new');

    const [showNewConversationModal, setshowNewConversationModal] = useState<boolean>(false);
    const [showNewStrengthModal, setShowNewStrengthModal] = useState<boolean>(false);
    const [strength, setStrength] = useState<StrengthList | undefined>(undefined);
    const [showUserConsentDialog, setShowUserConsentDialog] = useState<boolean>(false);
    const [showCAPCreationDialog, setShowCAPCreationDialog] = useState<boolean>(false);

    const [dateRange, setDateRange] = useState<Date[]>([]);
    const [selectedDate, setSelectedDate] = useState<string>('lastWeek');
    const rangeOptions = [
        { label: 'Last Week', value: 'lastWeek' },
        { label: 'Last Two Weeks', value: 'last2Weeks' },
        // { label: 'Last Year', value: 'lastYear' },
    ];
    const [aiMode, setAIMode] = useState<string>('');
    const [aiResponse, setAIResponse] = useState<string>('');
    const [loader, setLoader] = useState<boolean>(false);
    const [showAudioPlayer, setShowAudioPlayer] = useState<boolean>(false);
    const [strengthTitle, setStrengthTitle] = useState<string>('');
    const [strengthDescription, setStrengthDescription] = useState<string>('');
    const [discussionSubject, setDiscussionSubject] = useState<string>('');
    const [discussionDescription, setDiscussionDescription] = useState<string>('');
    const [discussionCoreValue, setDiscussionCoreValue] = useState<string>('');

    const { data: fileUrlData } = useGetRecordingStreamQuery(
        { fileName: currentRowData?.recording_file_name },
        { skip: !currentRowData?.recording_file_name }
    );

    const { data: recordingHistoryData } = useGetRecordingHistoryQuery(
        {
            employeeNumber: loggedEmpNum,
            fromDate: datetimeToDate(dateRange[0]),
        },
        {
            skip: !loggedEmpNum || !dateRange.length,
        }
    );

    const audioRef = useRef<HTMLAudioElement>(null);

    const selectedDateTemplate = (option: any) => {
        if (option) {
            return (
                <div className="flex align-items-center">
                    <i className="pi pi-calendar mr-2" style={{ paddingRight: '5px' }} />
                    {option.label}
                </div>
            );
        }
        return null;
    };

    const handleRangeChange = (e: any) => {
        const value = e.value;
        setSelectedDate(value);
        const calculatedDate = calculateDate(value);
        setDateRange(calculatedDate);
    };
    const calculateDate = (value: string): Date[] => {
        const currentDate = new Date();
        let calculatedDate = new Date();
        switch (value) {
            case 'lastWeek':
                calculatedDate.setDate(currentDate.getDate() - 7);
                break;
            case 'last2Weeks':
                calculatedDate.setDate(currentDate.getDate() - 14);
                break;
            case 'lastMonth':
                calculatedDate.setMonth(currentDate.getMonth() - 1);
                break;
            case 'last2Months':
                calculatedDate.setMonth(currentDate.getMonth() - 2);
                break;
            case 'lastYear':
                calculatedDate.setFullYear(currentDate.getFullYear() - 1);
                break;
            default:
                calculatedDate = currentDate;
                break;
        }
        return [calculatedDate, currentDate];
    };

    const extractBetween = (text: string, startDelimiter: string, endDelimiter: string) => {
        const startIndex = text?.indexOf(startDelimiter);
        if (startIndex === -1) return '';

        const contentStartIndex = startIndex + startDelimiter.length;
        const endIndex = text?.indexOf(endDelimiter, contentStartIndex);
        if (endIndex === -1) return '';

        return text?.substring(contentStartIndex, endIndex).trim();
    };

    const parseStrengthResponse = (responseString: string) => {
        const strengthTitle = extractBetween(responseString, '[TITLE_START]', '[TITLE_END]');

        const strengthDescription = extractBetween(responseString, '[DESC_START]', '[DESC_END]');
        return {
            strengthTitle,
            strengthDescription,
        };
    };

    const parseDiscussionResponse = (responseString: string) => {
        const discussionSubject = extractBetween(responseString, '[SUBJECT_START]', '[SUBJECT_END]');

        const discussionDescription = extractBetween(responseString, '[DESC_START]', '[DESC_END]');

        const discussionCoreValue = extractBetween(responseString, '[COREVALUE_START]', '[COREVALUE_END]');
        return {
            discussionSubject,
            discussionDescription,
            discussionCoreValue,
        };
    };


    const audioPlayerDialog = () => {
        return (
            <RecordingDialog
                visible={showAudioPlayer}
                onHide={() => setShowAudioPlayer(false)}
                fileUrl={fileUrlData?.data}
                currentRowData={currentRowData}
            />
        );
    };

    const discussionUserConsentDialog = () => {
        return (
            <UserConsentDialog
                visible={showUserConsentDialog}
                onClose={() => setShowUserConsentDialog(false)}
                onSmartGoal={() => {
                    AIExtractRecordingInsightsRequest(AiMode.Goals, currentRowData?.recording_file_name);
                }}
                onSummarize={() => {
                    AIExtractRecordingInsightsRequest(AiMode.Summarize, currentRowData?.recording_file_name);
                }}
            />
        );
    };
    const renderDiscussionModal = () => {
        if (viewState === 'read') {
            return (
                <ReadConversationLog
                    setViewState={setViewState}
                    setDisplayModal={setshowNewConversationModal}
                    viewState={viewState}
                    displayModal={showNewConversationModal}
                    authorId={loggedEmpNum}
                    authorName={loggedEmpUserName}
                    recieverId={currentRowData?.employee_number}
                    inputYear={currentRowData.year}
                    inputMonth={currentRowData.month}
                    receiverName={currentRowData?.employee_name}
                    via={CoachingTabs.RecordingHistory}
                    coachingEntityId={currentRowData?.coaching_recording_id}
                    isRecipient
                />
            );
        } else if (viewState === 'new') {
            return (
                <NewConversationLog
                    setViewState={setViewState}
                    setDisplayModal={setshowNewConversationModal}
                    viewState={viewState}
                    displayModal={showNewConversationModal}
                    authorId={loggedEmpNum}
                    authorName={loggedEmpUserName}
                    recieverId={currentRowData?.employee_number}
                    receiverName={currentRowData?.employee_name}
                    date={new Date()}
                    via={CoachingTabs.RecordingHistory}
                    coachingEntityId={currentRowData?.coaching_recording_id}
                    coachingEntityTypeId={CoachingEntityTypes.Recordings}
                    discussionDesc={discussionDescription}
                    discussionSubject={discussionSubject}
                    discussionCoreValue={discussionCoreValue}
                />
            );
        }
        return '';
    };
    const renderStrenghtsModal = () => {
        if (viewState === 'read') {
            return (
                <ReadStrengths
                    OnViewState={setViewState}
                    OnDisplayModal={setShowNewStrengthModal}
                    OnEditStrength={setStrength}
                    displayModal={showNewStrengthModal}
                    authorId={loggedEmpNum}
                    recieverId={currentRowData?.employee_number}
                    receiverName={currentRowData?.employee_name}
                    month={new Date(currentRowData?.startDate).getMonth() + 1}
                    year={new Date(currentRowData?.startDate).getFullYear()}
                    via={CoachingTabs.RecordingHistory}
                    coachingEntityId={currentRowData?.coaching_recording_id}
                />
            );
        } else if (viewState === 'new') {
            return (
                <NewStrength
                    OnViewState={setViewState}
                    OnDisplayModal={setShowNewStrengthModal}
                    displayModal={showNewStrengthModal}
                    authorId={loggedEmpNum}
                    authorName={loggedEmpUserName}
                    recieverId={currentRowData?.employee_number}
                    receiverName={currentRowData?.employee_name}
                    parentStrengthViewState={'new'}
                    month={new Date(currentRowData?.startDate).getMonth() + 1}
                    year={new Date(currentRowData?.startDate).getFullYear()}
                    via={CoachingTabs.RecordingHistory}
                    coachingEntityId={currentRowData?.coaching_recording_id}
                    coachingEntityTypeId={CoachingEntityTypes.Recordings}
                    strengthDesc={strengthDescription}
                    strengthTitle={strengthTitle}
                />
            );
        } else if (viewState === 'edit') {
            return (
                <UpdateStrength
                    OnViewState={setViewState}
                    OnDisplayModal={setShowNewStrengthModal}
                    displayModal={showNewStrengthModal}
                    strength={strength}
                    authorName={loggedEmpUserName}
                    recieverId={currentRowData?.employee_number}
                    receiverName={currentRowData?.employee_name}
                    month={strength?.strength_date ? new Date(strength?.strength_date).getMonth() + 1 : 0}
                    year={strength?.strength_date ? new Date(strength?.strength_date).getFullYear() : 0}
                />
            );
        }
        return '';
    };
    const renderCAPCreationModal = () => {
        return (
            <CAPCreationDialog
                visible={showCAPCreationDialog}
                onClose={() => setShowCAPCreationDialog(false)}
                recipientId={currentRowData?.employee_number}
                coachingEntityId={currentRowData?.coaching_recording_id}
                readonly={currentRowData?.caps}
                date={currentRowData?.recording_date}
                entityId={currentRowData?.coaching_recording_id}
                showCAPDialog={setShowCAPCreationDialog}
                recordingFileName={currentRowData?.recording_file_name}
            />
        );
    };

    const handleActionsClick = (rowData: any, actionName: string) => {
        setCurrentRowData(rowData);
        setAIResponse('');
        if (actionName === 'Strength') {
            setViewState(rowData.strengths ? 'read' : 'new');
            if (!rowData.strengths) {
                AIExtractRecordingInsightsRequest(AiMode.Strength, rowData?.recording_file_name);
            } else {
                setShowNewStrengthModal(true);
            }
        } else if (actionName === 'Discussion') {
            setViewState(rowData.discussions ? 'read' : 'new');
            setShowUserConsentDialog(true);
        } else if (actionName === 'CAPs') {
            setShowCAPCreationDialog(true);
        } else if (actionName === 'Audio') {
            setShowAudioPlayer(true);
            handlePlay(new MouseEvent('click') as unknown as React.MouseEvent);
        }
    };

    const dateTemplate = (rowData: any) => {
        return <span>{formatDate3(rowData?.recording_date)}</span>;
    };

    const createButtonsTemplate = (rowData: any, actionType: string) => {
        const buttons = [
            {
                actionName: 'Discussion',
                type: 'Discussion',
                color: 'var(--medium-blue)',
                borderColor: '#3299ff',
                backgroundColor: '#81C0FF80',
                actionCreated: rowData.discussions,
                title: rowData.discussions ? 'Discussion Created' : 'No Discussion created',
            },
            {
                actionName: 'Strength',
                type: 'Strength',
                color: 'var(--dark-purple)',
                borderColor: '#7d71e2',
                backgroundColor: '#D4D4FF',
                actionCreated: rowData.strengths,
                title: rowData.strengths ? 'Strength Created' : 'No Strength created',
            },
            {
                actionName: 'CAPs',
                type: 'CAPs',
                color: 'var(--dark-teal)',
                borderColor: '#00B6B2',
                backgroundColor: '#88FFF980',
                actionCreated: rowData.caps,
                title: rowData.caps ? 'CAPs Created' : 'No CAPs created',
            },
        ];

        const btn = buttons.find((b) => b.actionName === actionType);
        if (!btn) return null;

        return (
            <Button
                onClick={() => handleActionsClick(rowData, btn.actionName)}
                className="p-button-outlined"
                style={{
                    padding: '6px 12px',
                    borderRadius: '30px',
                    borderColor: btn.borderColor,
                    color: btn.color,
                    backgroundColor: btn.actionCreated ? btn.backgroundColor : 'transparent',
                }}
                title={btn.title}
            >
                <i className={btn.actionCreated ? 'pi pi-eye' : 'pi pi-plus-circle'} />
                <Divider
                    style={{
                        height: '21px',
                        margin: '0 0px',
                        width: '10px',
                        transform: 'rotate(270deg)',
                    }}
                />
                <span style={{ fontWeight: 600 }}>{btn.type}</span>
            </Button>
        );
    };

    const summaryBodyTemplate = (rowData: any) => {
        return (
            <div
                // style={{
                //     whiteSpace: 'nowrap',
                //     overflow: 'hidden',
                //     textOverflow: 'ellipsis',
                //     maxWidth: '200px', // adjust as needed
                //     cursor: 'pointer',
                // }}
                title={rowData.recording_description} // tooltip with full content
            >
                {rowData.recording_description}
            </div>
        );
    };

    useEffect(() => {
        const calculatedDate = calculateDate('lastWeek');
        setDateRange(calculatedDate);
    }, []);

    useEffect(() => {
        if (!showNewConversationModal) {
            setShowUserConsentDialog(false);
            setAIResponse('');
            setAIMode('');
        }
    }, [showNewConversationModal]);

    useEffect(() => {
        if (aiMode !== '' && aiResponse !== '') {
            if (aiMode === 'Strength') {
                const { strengthTitle, strengthDescription } = parseStrengthResponse(aiResponse);
                setStrengthTitle(strengthTitle);
                setStrengthDescription(strengthDescription);
                setShowNewStrengthModal(true);
            } else {
                const { discussionSubject, discussionDescription, discussionCoreValue } = parseDiscussionResponse(aiResponse);
                setDiscussionSubject(discussionSubject);
                setDiscussionDescription(discussionDescription);
                setDiscussionCoreValue(discussionCoreValue);
                setshowNewConversationModal(true);
            }
        }
    }, [aiMode, aiResponse]);

    const handlePlay = (e: React.MouseEvent) => {
        e.preventDefault();
        const signedUrl = fileUrlData?.data;
        if (audioRef.current) {
            audioRef.current.src = signedUrl;
            audioRef.current.play();
        }
    };

    const recordingTemplate = (rowData: any) => {
        return (
            <button
                type="button"
                onClick={() => handleActionsClick(rowData, 'Audio')}
                onKeyDown={(e) => {
                    if (e.key === 'Enter' || e.key === ' ') {
                        e.preventDefault();
                        const mouseEvent = new MouseEvent('click', {
                            bubbles: true,
                            cancelable: true,
                            view: window,
                        });
                        (e.target as HTMLElement).dispatchEvent(mouseEvent);
                    }
                }}
                style={{
                    color: '#2e74d1',
                    textDecoration: 'underline',
                    cursor: 'pointer',
                    background: 'none',
                    border: 'none',
                    padding: 0,
                    font: 'inherit',
                    textAlign: 'left',
                }}
            >
                {rowData.recording_title}
            </button>
        );
    };
    const AIExtractRecordingInsightsRequest = (mode: string, fileName: string | null) => {
        setAIMode(mode);
        if (mode !== '') {
            const data = {
                fileName,
                mode: mode ?? null,
                maxLength: 3900,
            };
            setLoader(true);
            AIExtractRecordingInsights(data)
                .unwrap()
                .then((res: any) => {
                    if (res) {
                        setAIResponse(res?.response?.replace(/(##|\*\*)/g, ''));
                        setViewState('new');
                    }
                    setLoader(false);
                })
                .then((error: any) => {
                    console.log(error);
                });
        }
    };

    return (
        <div id="recordingHistoryId" className="layout-wrapper">
            <div className="layout-content">
                <div className="main-content">
                    <div className="custom-filter-container">
                        <div className="filter-box">
                            <label htmlFor="date">Time Range</label>
                            <Dropdown
                                className="w-full md:w-20rem custom-dropdown"
                                value={selectedDate}
                                options={rangeOptions}
                                onChange={handleRangeChange}
                                placeholder="Select Range"
                                valueTemplate={selectedDateTemplate}
                                optionLabel="label"
                                optionValue="value"
                            />
                        </div>
                    </div>
                    <div style={{
                        color: '#C90370',
                        fontFamily: 'Urbanist',
                        fontStyle: 'italic',
                        fontSize: '16px',
                        fontWeight: 800,
                        margin: '12px 0 8px 0',
                    }}>
                        Recordings will only be available for 14 days from the date of creation.
                    </div>
                    <div className="main-sections">
                        <div className="sections-row">
                            <DataTable
                                value={recordingHistoryData?.data}
                                style={{ marginBottom: '32px' }}
                                emptyMessage="No records found."
                                rows={10}
                                className="recordingHistory"
                                paginator={recordingHistoryData?.data?.length > 10}
                                paginatorTemplate="FirstPageLink PrevPageLink PageLinks NextPageLink LastPageLink CurrentPageReport RowsPerPageDropdown"
                                currentPageReportTemplate="Showing {first} to {last} of {totalRecords} entries"
                            >
                                <Column header="Associate" field="employee_name" sortable />
                                <Column
                                    header="Date"
                                    sortField="recording_date"
                                    body={dateTemplate}
                                    sortable
                                />
                                <Column
                                    header="Recording"
                                    body={recordingTemplate}
                                    field="recording_title"
                                    sortable
                                />
                                <Column header="Duration" field="recording_length" />
                                <Column
                                    header="Discussion"
                                    body={(rowData) => createButtonsTemplate(rowData, 'Discussion')}
                                />
                                {!isAllowCoachingActionModel && <Column header="Strength's" body={(rowData) => createButtonsTemplate(rowData, 'Strength')} />}
                                {!isAllowCoachingActionModel && <Column header="CAP's" body={(rowData) => createButtonsTemplate(rowData, 'CAPs')} />}
                                <Column header="Summary" body={summaryBodyTemplate} />
                            </DataTable>
                        </div>
                    </div>
                </div>
            </div>
            {showNewConversationModal && renderDiscussionModal()}
            {showNewStrengthModal && renderStrenghtsModal()}
            {showUserConsentDialog && discussionUserConsentDialog()}
            {showCAPCreationDialog && renderCAPCreationModal()}
            {showAudioPlayer && audioPlayerDialog()}
            <div>{loader ? <LoadingSpinner /> : ''}</div>
        </div>
    );
};
